/**
 * Project invite-link generation/redemption — carved out of the old
 * MongoDB-backed syncService (see mia-flow-bff's lib/invite-tokens.js for
 * the full rationale: this is a separate, unrelated access-control feature
 * that happened to be bundled into the same "sync manager" as real-time
 * collaboration, which moved to vfs-service's own Yjs infrastructure).
 */

import config from '../config';
import { getBffHeaders } from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';

/**
 * Generate (or refresh) an invite token for a project.
 * @param {string} projectId
 * @param {string} ownerId
 * @param {number} expiresInHours
 * @returns {Promise<{token: string, expiresAt: string, inviteLink: string}>}
 */
export async function generateInviteToken(projectId, ownerId, expiresInHours = 24) {
  const response = await getAuthorizedFetch()(`${config.bffUrl}/api/invites/projects/${encodeURIComponent(projectId)}`, {
    method: 'POST',
    headers: getBffHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      ownerId,
      projectName: projectId,
      expiresInHours,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to generate invite: ${response.statusText}`);
  }

  const result = await response.json();
  const token = result.data?.inviteToken;

  return {
    token,
    expiresAt: result.data?.inviteTokenExpiresAt,
    inviteLink: `${window.location.origin}/join/${token}`,
  };
}

/**
 * Redeem an invite token.
 * @param {string} token
 * @param {string} userId
 * @returns {Promise<{projectId: string, projectName: string}>}
 */
export async function joinByToken(token, userId) {
  const response = await getAuthorizedFetch()(`${config.bffUrl}/api/invites/join/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: getBffHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ userId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to join project');
  }

  const result = await response.json();
  return result.data;
}
